import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Award, CheckCircle, Users, ClipboardList, Target, ArrowRight, Stethoscope, HeartHandshake } from 'lucide-react';

const levels = [
  {
    level: 4,
    title: 'Certified Aesthetic Clinical Assistant',
    category: 'Clinical Support Certification',
    categoryColor: 'bg-slate-100 text-slate-700 border-slate-200',
    badgeColor: 'bg-slate-100 text-slate-700 border-slate-200',
    purpose: 'Foundation certification for individuals providing clinical support within aesthetic and medical practice under the supervision of a licensed healthcare practitioner.',
    eligibility: [
      'Clinical Assistants',
      'Medical Assistants',
      'Dental Assistants',
      'Operation Theatre Assistants',
      'Aesthetic Clinic Assistants',
      'Healthcare Technicians',
      'Other clinical support personnel working within their legal scope',
    ],
    assessment: [
      'Foundation Written Examination (MCQs)',
      'Basic Clinical Skills OSCE',
      'Infection Prevention & Patient Safety Assessment',
      'Professional Ethics Assessment',
    ],
    outcomeLabel: 'Demonstrates competency in',
    outcomes: [
      'Patient preparation',
      'Clinical assistance',
      'Infection control',
      'Safety protocols',
      'Professional conduct',
    ],
  },
  {
    level: 5,
    title: 'Advanced Aesthetic Assistant',
    category: 'Clinical Support Certification',
    categoryColor: 'bg-slate-100 text-slate-700 border-slate-200',
    badgeColor: 'bg-blue-50 text-blue-700 border-blue-200',
    purpose: 'Advanced certification for experienced clinical support professionals performing expanded clinical responsibilities under supervision.',
    eligibility: [
      'Certified Clinical Assistants',
      'Senior Medical Assistants',
      'Experienced Dental Assistants',
      'Registered Nurses (where legally applicable)',
      'Clinical Therapists and Technicians working under licensed practitioners',
    ],
    assessment: [
      'Advanced Written Examination',
      'Procedural Assistance OSCE',
      'Clinical Logbook Review',
      'Supervisor Competency Evaluation',
      'Professional Behaviour Assessment',
    ],
    outcomeLabel: 'Demonstrates competency in',
    outcomes: [
      'Advanced procedural assistance',
      'Device familiarity',
      'Clinical workflow management',
      'Patient communication',
      'Team-based clinical practice',
    ],
  },
  {
    level: 6,
    title: 'Professional Clinical Certification',
    category: 'Practitioner Certification',
    categoryColor: 'bg-green-50 text-green-700 border-green-200',
    badgeColor: 'bg-green-50 text-green-700 border-green-200',
    purpose: 'Professional practitioner-level certification for registered healthcare professionals demonstrating independent clinical competency.',
    eligibilityIntro: 'Registered Healthcare Practitioners holding valid registration or licensure (where applicable), including:',
    eligibility: [
      'Medical Doctors',
      'Dentists',
      'Registered Nurses',
      'Pharmacists (where applicable)',
      'Physician Associates/Assistants',
      'Other licensed healthcare professionals accepted under IMCAC eligibility criteria',
    ],
    assessment: [
      'Comprehensive Written Examination',
      'Advanced Clinical OSCE',
      'Case-Based Discussion',
      'Oral Viva Examination',
      'Clinical Case Log Verification',
    ],
    outcomeLabel: 'Demonstrates competency in',
    outcomes: [
      'Independent clinical decision-making',
      'Patient assessment',
      'Safe procedural practice',
      'Complication management',
      'Evidence-based clinical practice',
    ],
  },
  {
    level: 7,
    title: 'Board-Certified Advanced Clinical Level',
    category: 'Board Certification',
    categoryColor: 'bg-purple-50 text-purple-700 border-purple-200',
    badgeColor: 'bg-purple-50 text-purple-700 border-purple-200',
    purpose: 'Advanced board certification recognizing mastery across multiple competency domains.',
    eligibilityIntro: 'Registered practitioners who:',
    eligibility: [
      'Hold Level 6 certification (or approved equivalent)',
      'Meet clinical experience requirements',
      'Fulfil board eligibility criteria',
    ],
    assessment: [
      'Advanced Board Written Examination',
      'Complex Clinical OSCE',
      'Expert Viva Examination',
      'Portfolio Review',
      'Clinical Audit',
      'Peer Competency Assessment',
    ],
    outcomeLabel: 'Demonstrates mastery in',
    outcomes: [
      'Advanced patient management',
      'Complex case planning',
      'Complication recognition and management',
      'Clinical leadership',
      'Professional excellence',
    ],
  },
  {
    level: 8,
    title: 'Fellowship & Academic Leadership Level',
    category: 'Fellowship',
    categoryColor: 'bg-[#C5A572]/10 text-[#C5A572] border-[#C5A572]/30',
    badgeColor: 'bg-[#C5A572]/10 text-[#C5A572] border-[#C5A572]/30',
    purpose: 'The highest level of professional recognition awarded to practitioners demonstrating excellence in clinical practice, education, research, and leadership.',
    eligibility: [
      'Board-certified practitioners meeting IMCAC Fellowship eligibility requirements',
    ],
    assessment: [
      'Fellowship Portfolio Review',
      'Research Dissertation or Thesis',
      'Publications and Academic Contributions',
      'Teaching Assessment',
      'Curriculum Development',
      'Academic Presentation',
      'Leadership Interview',
      'Peer Review',
    ],
    outcomeLabel: 'Recognizes excellence in',
    outcomes: [
      'Academic leadership',
      'Research',
      'Teaching',
      'Curriculum development',
      'Professional mentorship',
      'Innovation in healthcare',
    ],
  },
];

const pathways = [
  {
    id: 'clinical-support',
    icon: HeartHandshake,
    title: 'Clinical Support Professional Pathway',
    steps: ['Level 4', 'Level 5'],
    desc: 'Designed for assistants and clinical support professionals working under the supervision of licensed healthcare practitioners.',
    exams: [
      { code: 'CBEAA', label: 'Clinical Assistance', to: '/allied-faculty/clinical-assistance' },
      { code: 'CBENA', label: 'Aesthetic Nursing', to: '/allied-faculty/aesthetic-nursing' },
    ],
  },
  {
    id: 'practitioner',
    icon: Stethoscope,
    title: 'Registered Healthcare Practitioner Pathway',
    steps: ['Level 6', 'Level 7', 'Level 8'],
    desc: 'Designed for registered or licensed healthcare practitioners seeking competency-based professional certification and career advancement.',
    exams: [
      { code: 'CBEAM', label: 'Aesthetic Medicine', to: '/faculty/aesthetic-medicine' },
      { code: 'CBELM', label: 'Longevity Medicine', to: '/faculty/longevity-medicine' },
      { code: 'CBERM', label: 'Regenerative Medicine', to: '/faculty/regenerative-medicine' },
      { code: 'CBEAD', label: 'Aesthetic Dentistry', to: '/dental-faculty/aesthetic-dentistry' },
      { code: 'CBE-LMM', label: 'Longevity, Metabolic & Nutraceutical', to: '/examination/cbe-lmm' },
    ],
  },
];

function DetailSection({ icon: Icon, title, intro, items, green = false }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-[#C5A572]" />
        <h4 className="text-sm font-semibold text-[#1B2A4A] uppercase tracking-wide">{title}</h4>
      </div>
      {intro && <p className="text-sm text-slate-500 mb-2">{intro}</p>}
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            {green ? (
              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <span className="w-1.5 h-1.5 bg-[#C5A572] rounded-full flex-shrink-0 mt-1.5" />
            )}
            <span className="text-sm text-slate-600 leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function IPCFPage() {
  return (
    <div data-testid="ipcf-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Professional Standards</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">
            IMCAC Professional Certification Framework
          </h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            The IPCF defines competency-based professional benchmarks for practitioners at all stages of their career in aesthetic, longevity, and regenerative medicine.
          </p>
        </div>
      </section>

      {/* Certification Levels */}
      <section className="imcac-section bg-white">
        <div className="imcac-container max-w-4xl">
          <div className="mb-10">
            <p className="imcac-overline mb-3">Certification Levels</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1B2A4A] font-heading">
              A Structured Progression of Professional Competency
            </h2>
            <p className="text-sm text-slate-500 mt-3 max-w-2xl">
              Five certification levels spanning clinical support roles through practitioner certification, board certification, and fellowship recognition.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {levels.map((l) => (
              <AccordionItem
                key={l.level}
                value={`level-${l.level}`}
                className="border border-slate-200 rounded-sm bg-white shadow-sm hover:border-[#C5A572]/50 transition-colors data-[state=open]:border-[#C5A572]"
                data-testid={`ipcf-level-${l.level}`}
              >
                <AccordionTrigger className="px-5 py-5 hover:no-underline" data-testid={`ipcf-level-${l.level}-trigger`}>
                  <div className="flex items-start gap-4 text-left w-full">
                    <div className="w-12 h-12 bg-[#1B2A4A] rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-base font-bold text-[#C5A572] font-heading">{l.level}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <Badge className={`text-xs rounded-sm border ${l.badgeColor}`}>Level {l.level}</Badge>
                        <Badge variant="outline" className={`text-[10px] rounded-sm border uppercase tracking-wider ${l.categoryColor}`} data-testid={`ipcf-level-${l.level}-category`}>
                          {l.category}
                        </Badge>
                      </div>
                      <h3 className="text-base md:text-lg font-semibold text-[#1B2A4A] font-heading">{l.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed mt-1 pr-4">{l.purpose}</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-6">
                  <div className="border-t border-slate-100 pt-5 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    <DetailSection
                      icon={Users}
                      title="Eligible Applicants"
                      intro={l.eligibilityIntro}
                      items={l.eligibility}
                    />
                    <DetailSection
                      icon={ClipboardList}
                      title="Assessment Framework"
                      items={l.assessment}
                    />
                    <DetailSection
                      icon={Target}
                      title={l.outcomeLabel}
                      items={l.outcomes}
                      green
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Professional Pathways */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container max-w-4xl">
          <div className="text-center mb-10">
            <p className="imcac-overline mb-3">Career Progression</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1B2A4A] font-heading">Professional Pathways</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="professional-pathways">
            {pathways.map((p) => (
              <Card key={p.id} className="imcac-card border-t-4 border-t-[#C5A572]" data-testid={`pathway-${p.id}`}>
                <CardContent className="p-6 md:p-8">
                  <div className="w-11 h-11 bg-[#1B2A4A] rounded-sm flex items-center justify-center mb-5">
                    <p.icon className="h-5 w-5 text-[#C5A572]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">{p.title}</h3>
                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    {p.steps.map((step, i) => (
                      <div key={step} className="flex items-center gap-2">
                        <span className="px-3 py-1.5 bg-[#1B2A4A] text-[#C5A572] text-xs font-bold rounded-sm font-heading">{step}</span>
                        {i < p.steps.length - 1 && <ArrowRight className="h-4 w-4 text-[#C5A572]" />}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                  <div className="border-t border-slate-100 mt-5 pt-4">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Relevant Examinations</p>
                    <div className="flex flex-wrap gap-2">
                      {p.exams.map((exam) => (
                        <Link key={exam.code} to={exam.to} data-testid={`pathway-exam-${exam.code.toLowerCase()}`}>
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-sm text-xs text-slate-600 hover:border-[#C5A572] hover:text-[#1B2A4A] transition-colors group">
                            <span className="font-bold text-[#1B2A4A]">{exam.code}</span>
                            <span className="text-slate-400 group-hover:text-slate-600">· {exam.label}</span>
                            <ArrowRight className="h-3 w-3 text-[#C5A572]" />
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="pb-16 md:pb-20 bg-[#F8FAFC]">
        <div className="imcac-container max-w-4xl">
          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="ipcf-important-note">
            <div className="flex items-start gap-3">
              <Award className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-[#1B2A4A] font-heading mb-2">Important Note</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  The IMCAC Professional Certification Framework (IPCF) is a competency-based professional certification system designed to support structured career progression. It does not replace national licensure, professional registration, academic qualifications, or specialist recognition. Certification does not grant independent practice rights or expand a participant's legal scope of practice. Levels 4–5 are intended for clinical support professionals working under appropriate supervision, while Levels 6–8 are reserved for registered or licensed healthcare practitioners who satisfy IMCAC eligibility requirements. All certificants are responsible for complying with the laws, regulations, and professional standards governing healthcare practice in their respective jurisdictions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
